<template>
  <div>
    <core-popup
      :show="isPopupVisible"
      :title="''"
      width="940"
      class-list="learning_center_popup_new"
      :body-style="'noPaddingRight'"
      @onClickCancel="handleClose"
    >
      <template #body>
        <div :class="$style['learning-center-wrapper']">
          <div :class="$style.header">
            <div :class="$style.header__left">
              <h2 :class="$style.header__title">
                {{ locales('app_learnwnd_caption') }}
              </h2>
            </div>
            <div :class="$style.header__center">
              <learning-center-search
                v-model="searchQuery"
                :is-no-results="isSearchNoResults"
                @tutorial-click="handleSearchTutorialClick"
                @help-center-search="handleSearchSubmit"
              />
            </div>
            <div :class="$style.header__right">
              <div :class="$style.header__close_icon">
                <svg-sprite
                  :name="'close-1'"
                  :size="24"
                  :type="'regular'"
                  color="#999"
                  @onClick="handleClose"
                />
              </div>
            </div>
          </div>

          <!-- Main content: Sidebar + Content -->
          <div :class="$style.content">
            <!-- Left sidebar -->
            <learning-center-sidebar
              :active-section="currentSection"
              :has-unread-news="hasUnreadNews"
              @section-change="handleSectionChange"
            />

            <!-- Main content area -->
            <div :class="$style['main-content']">
              <keep-alive>
                <component
                  :is="currentSectionComponent"
                  :search-query="searchQuery"
                  :has-unread-news="hasUnreadNews"
                  @open-video="handleOpenVideo"
                />
              </keep-alive>
            </div>
          </div>
        </div>
      </template>
    </core-popup>

    <!-- Video popup -->
    <learning-center-video-popup
      :show="isVideoPopupVisible"
      :youtube-id="currentVideoId"
      @close="handleCloseVideo"
    />
  </div>
</template>

<script>
import vgpIconButton from '$$vueCmp/globalButton/withIcon/iconButton.vue';
import CorePopup from '../../common/VueComponents/popups/corePopup/corePopup.vue';

import constants from '../../../helpers/constants';
import { POPUP_TAB, SIDEBAR_SECTIONS } from '../learningCenter.config';
import LearningCenterSidebar from './components/learningCenterSidebar.vue';
import LearningCenterVideoPopup from './components/popups/learningCenterVideoPopup.vue';
import LearningCenterSearch from './components/search/learningCenterSearch.vue';
import LearningCenterSearchNoResults from './components/search/learningCenterSearchNoResults.vue';
import LearningCenterFeedbackTab from './components/tabs/feedbackTab/learningCenterFeedbackTab.vue';
import LearningCenterTranslationErrorTab from './components/tabs/feedbackTab/learningCenterTranslationErrorTab.vue';
import LearningCenterGuideTabWrapper from './components/tabs/guideTab/learningCenterGuideTabWrapper.vue';
import LearningCenterMobileTab from './components/tabs/mobileTab/learningCenterMobileTab.vue';
import LearningCenterNewsTab from './components/tabs/newsTab/learningCenterNewsTab.vue';
import LearningCenterTutorialsTab from './components/tabs/tutorialsTab/learningCenterTutorialsTab.vue';

export default {
  name: 'LearningCenterPopupNew',
  components: {
    CorePopup,
    vgpIconButton,
    LearningCenterSidebar,
    LearningCenterSearch,
    LearningCenterVideoPopup,
    LearningCenterSearchNoResults,
    [POPUP_TAB.GUIDE]: LearningCenterGuideTabWrapper,
    [POPUP_TAB.TUTORIALS]: LearningCenterTutorialsTab,
    [POPUP_TAB.NEWS]: LearningCenterNewsTab,
    [SIDEBAR_SECTIONS.FEEDBACK]: LearningCenterFeedbackTab,
    [SIDEBAR_SECTIONS.MOBILE]: LearningCenterMobileTab,
    [SIDEBAR_SECTIONS.TRANSLATION_ERROR]: LearningCenterTranslationErrorTab,
  },
  watch: {
    searchQuery(newQuery, oldQuery) {
      const wasNoResults = this.isSearchNoResults;
      this.isSearchNoResults = false;

      if (wasNoResults && this.currentSection === SIDEBAR_SECTIONS.HELP) {
        this.isReturningFromSearch = true;
        const targetSection = (this.previousSection && this.previousSection !== SIDEBAR_SECTIONS.HELP)
          ? this.previousSection
          : SIDEBAR_SECTIONS.TUTORIALS;
        this.currentSection = targetSection;
        this.$nextTick(() => {
          this.isReturningFromSearch = false;
        });
      }
    },
    async currentSection(newSection, oldSection) {
      if (oldSection === SIDEBAR_SECTIONS.NEWS && newSection !== SIDEBAR_SECTIONS.NEWS && this.hasUnreadNews) {
        await this.$store.dispatch('user/updateActivityStatus', {
          activityName: constants.USER_ACTIVITIES.LAST_NEWS_ID,
          status: window.user.lastNewsEntryId,
        });
        this.$nextTick(() => {
          this.activityUpdateKey += 1;
        });
      }
      if (!this.isReturningFromSearch && (newSection !== SIDEBAR_SECTIONS.HELP || !this.isSearchNoResults)) {
        this.previousSection = oldSection;
      }
    },
  },
  props: {
    initialSection: {
      type: String,
      default: null,
    },
  },
  emits: [
    'close',
  ],
  data() {
    return {
      locales: __,
      currentSection: SIDEBAR_SECTIONS.TUTORIALS,
      previousSection: SIDEBAR_SECTIONS.TUTORIALS,
      searchQuery: '',
      isPopupVisible: true,
      isVideoPopupVisible: false,
      currentVideoId: '',
      isSearchNoResults: false,
      activityUpdateKey: 0,
      isReturningFromSearch: false,
    };
  },
  computed: {
    hasUnreadNews() {
      this.activityUpdateKey; // eslint-disable-line no-unused-expressions
      const viewActivity = window.user.activity.find(entry => entry.name === 'lastViewedNewsEntryId');
      const lastDisplayedActivityId = viewActivity && viewActivity.activity_status;
      return lastDisplayedActivityId !== window.user.lastNewsEntryId;
    },
    currentSectionComponent() {
      if (this.currentSection === SIDEBAR_SECTIONS.HELP && this.isSearchNoResults) {
        return 'LearningCenterSearchNoResults';
      }

      const sectionMap = {
        [SIDEBAR_SECTIONS.TUTORIALS]: POPUP_TAB.TUTORIALS,
        [SIDEBAR_SECTIONS.NEWS]: POPUP_TAB.NEWS,
        [SIDEBAR_SECTIONS.HELP]: POPUP_TAB.GUIDE,
        [SIDEBAR_SECTIONS.FEEDBACK]: SIDEBAR_SECTIONS.FEEDBACK,
        [SIDEBAR_SECTIONS.MOBILE]: SIDEBAR_SECTIONS.MOBILE,
        [SIDEBAR_SECTIONS.TRANSLATION_ERROR]: SIDEBAR_SECTIONS.TRANSLATION_ERROR,
      };
      return sectionMap[this.currentSection] || POPUP_TAB.TUTORIALS;
    },
  },
  mounted() {
    userExtAnalytics.log('learning_center_window_open');
    if (this.initialSection) {
      this.currentSection = this.initialSection;
      this.previousSection = this.initialSection;
    } else if (this.hasUnreadNews) {
      this.currentSection = SIDEBAR_SECTIONS.NEWS;
      this.previousSection = SIDEBAR_SECTIONS.NEWS;
    } else {
      this.previousSection = this.currentSection;
    }
  },
  beforeUnmount() {
    userExtAnalytics.log('learning_center_window_hide');
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    handleSectionChange(section) {
      this.currentSection = section;
      userExtAnalytics.log('learning_center_section_change', { section });
    },
    handleOpenVideo(youtubeId) {
      this.currentVideoId = youtubeId;
      this.isPopupVisible = false;
      this.isVideoPopupVisible = true;
    },
    handleCloseVideo() {
      this.isVideoPopupVisible = false;
      this.isPopupVisible = true;
      this.currentVideoId = '';
    },
    handleSearchTutorialClick(video) {
      if (video && video.youtubeId) {
        this.handleOpenVideo(video.youtubeId);
      }
    },
    handleSearchSubmit(payload) {
      const query = typeof payload === 'string' ? payload : payload?.query;
      const hasResults = typeof payload === 'string' ? true : !!payload?.hasResults;

      const normalizedQuery = (query || '').toLowerCase().trim();

      if (!normalizedQuery) {
        this.isSearchNoResults = false;
        return;
      }
      if (!hasResults) {
        if (this.currentSection !== SIDEBAR_SECTIONS.HELP) {
          this.previousSection = this.currentSection;
        } else if (!this.previousSection || this.previousSection === SIDEBAR_SECTIONS.HELP) {
          this.previousSection = SIDEBAR_SECTIONS.TUTORIALS;
        }
        // Set flag before switching to prevent watcher from updating previousSection
        this.isSearchNoResults = true;
        this.currentSection = SIDEBAR_SECTIONS.HELP;
      } else {
        this.isSearchNoResults = false;
      }
    },
  },
};
</script>

<style module src="./less/learningCenterPopupNew.less" lang="less">
</style>

